import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  Shoes: 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-shirt-line',
  'T-Shirts': 'ri-t-shirt-line',
  Jackets: 'ri-t-shirt-air-line',
  'Pants/Shorts': 'ri-archive-line',
  Headwear: 'ri-customer-service-line',
  Sets: 'ri-stack-line',
  'Underwear/Underpants': 'ri-checkbox-blank-circle-line',
  Jersey: 'ri-shirt-line',
  Accessories: 'ri-glasses-line',
  Bags: 'ri-handbag-line',
  Belts: 'ri-link',
  Perfumes: 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const half = Math.ceil(categories.length / 2);
  const leftCol = categories.slice(0, half);
  const rightCol = categories.slice(half);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md">
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-label font-semibold text-sm text-foreground-950 whitespace-nowrap">Kakobuy Finds Polska</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center gap-1.5 font-label text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line text-sm"></i>
              Strona Główna
            </Link>

            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="flex items-center gap-1.5 font-label text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span className="w-2 h-2 rounded-full bg-foreground-950 inline-block"></span>
                Kategorie
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-[560px] bg-background-50 rounded-lg border border-background-200/80 shadow-sm p-5">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                    {[leftCol, rightCol].map((col, ci) => (
                      <div key={ci} className="flex flex-col gap-0.5">
                        {col.map((cat) => (
                          <a
                            key={cat.slug}
                            href={cat.url}
                            target="_blank"
                            rel="noopener nofollow"
                            className="flex items-center gap-2 px-3 py-2 rounded-md font-body text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                          >
                            <i className={`${categoryIcons[cat.slug] || 'ri-store-line'} text-xs text-foreground-400`}></i>
                            {cat.name}
                          </a>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/artykul/kakobuy-zakupy-poradnik"
              className="flex items-center gap-1.5 font-label text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line text-sm"></i>
              Poradnik Zakupów
            </Link>

            <Link to="/artykul/kakobuy-spreadsheet-poradnik" className="font-label text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Poradnik
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="font-label text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Kakobuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 font-label text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl text-foreground-950 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-5 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-background-200/60">
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-2.5 bg-foreground-950 text-background-50 font-label text-sm font-semibold rounded-full whitespace-nowrap"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line text-sm"></i>
              Strona Główna
            </Link>
            <Link
              to="/artykul/kakobuy-spreadsheet-poradnik"
              className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 font-label text-sm font-semibold rounded-full whitespace-nowrap"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line text-sm"></i>
              Poradnik
            </Link>
          </div>

          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              <p className="font-label text-xs font-semibold text-foreground-500 uppercase tracking-wider">Kategorie Produktów</p>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg font-body text-sm text-foreground-700 hover:text-foreground-950 hover:bg-accent-50/40 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${categoryIcons[cat.slug] || 'ri-store-line'} text-foreground-500 text-xs`}></i>
                  </div>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4 border-t border-background-200/60">
            <Link
              to="/artykul/kakobuy-zakupy-poradnik"
              className="flex items-center gap-2 font-label text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors px-3 py-2"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-shopping-bag-3-line text-sm"></i>
              Poradnik Zakupów
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 font-label text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors px-3 py-2"
            >
              <i className="ri-external-link-line text-sm"></i>
              Kakobuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-primary-500 text-background-50 font-label text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap mt-1"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}